import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { Liability, IncomeSource } from '../../types/buyerFinancialData';

interface DebtToIncomeChartProps {
  liabilities: Liability[];
  incomeSources: IncomeSource[];
  className?: string;
}

export function DebtToIncomeChart({ liabilities, incomeSources, className = '' }: DebtToIncomeChartProps) {
  // Calculate monthly income
  const monthlyIncome = incomeSources.reduce((sum, income) => sum + income.monthlyAmount, 0);
  
  // Calculate monthly debt payments
  const monthlyDebtPayments = liabilities.reduce((sum, liability) => sum + liability.monthlyPayment, 0);
  
  // Calculate debt-to-income ratio
  const debtToIncomeRatio = monthlyIncome > 0 ? (monthlyDebtPayments / monthlyIncome) * 100 : 0;
  
  // Calculate remaining income after debt payments
  const remainingIncome = monthlyIncome - monthlyDebtPayments;
  
  // Prepare data for pie chart
  const pieData = [
    { name: 'Debt Payments', value: monthlyDebtPayments, color: '#ef4444' },
    { name: 'Remaining Income', value: remainingIncome, color: '#10b981' }
  ];

  // Prepare data for debt breakdown
  const debtBreakdown = liabilities.map(liability => ({
    name: liability.description,
    value: liability.monthlyPayment,
    percentage: monthlyIncome > 0 ? (liability.monthlyPayment / monthlyIncome) * 100 : 0,
    balance: liability.currentBalance,
    interestRate: liability.interestRate
  }));

  // Custom tooltip for pie chart
  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = monthlyIncome > 0 ? (data.value / monthlyIncome) * 100 : 0;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{data.name}</p>
          <p className="text-sm text-gray-600">
            ${data.value.toLocaleString()} ({percentage.toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for debt breakdown
  const DebtTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{data.name}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Monthly Payment:</span>
              <span className="font-semibold">${data.value.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Balance:</span>
              <span className="font-semibold">${data.balance.toLocaleString()}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Interest Rate:</span>
              <span className="font-semibold">{data.interestRate}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // SBA requirement status
  const meetsSBARequirement = debtToIncomeRatio < 40;
  const sbaStatusColor = meetsSBARequirement ? 'text-green-600' : 'text-red-600';
  const sbaStatusBg = meetsSBARequirement ? 'bg-green-50' : 'bg-red-50';

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Debt-to-Income Analysis</h3>
        <p className="text-sm text-gray-600">
          Monthly income allocation showing debt payments vs. remaining income. SBA typically requires DTI ratio below 40%.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={120}
                paddingAngle={5}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Debt Breakdown */}
        <div className="space-y-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-gray-900 mb-1">
              {debtToIncomeRatio.toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600 mb-2">Debt-to-Income Ratio</div>
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${sbaStatusBg} ${sbaStatusColor}`}>
              {meetsSBARequirement ? 'Meets SBA Requirement' : 'Exceeds SBA Requirement'}
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Debt Breakdown</h4>
            {debtBreakdown.map((debt, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900 text-sm">{debt.name}</div>
                  <div className="text-xs text-gray-600">
                    {debt.percentage.toFixed(1)}% of income
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900">
                    ${debt.value.toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-600">
                    {debt.interestRate}% APR
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-3 gap-4 pt-4 border-t border-gray-100">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">
            ${(monthlyIncome / 1000).toFixed(0)}k
          </div>
          <div className="text-sm text-gray-600">Monthly Income</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-600">
            ${(monthlyDebtPayments / 1000).toFixed(0)}k
          </div>
          <div className="text-sm text-gray-600">Monthly Debt Payments</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">
            ${(remainingIncome / 1000).toFixed(0)}k
          </div>
          <div className="text-sm text-gray-600">Remaining Income</div>
        </div>
      </div>
    </div>
  );
}

